import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { listMine } from '../services/userDevices';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { BatteryBar } from '../components/ui/BatteryBar';
import { useAuth } from '../hooks/useAuth';

export function MyDevicesPage() {
  const { user } = useAuth();
  const myDevices = useQuery({
    queryKey: ['me', 'devices'],
    queryFn: listMine,
  });

  const sorted = useMemo(() => {
    if (!myDevices.data) return [];
    return [...myDevices.data].sort((a, b) => a.batteryHealthPct - b.batteryHealthPct);
  }, [myDevices.data]);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-end justify-between">
        <div>
          <h1>My devices</h1>
          <p className="text-sm text-neutral-400">
            {user?.displayName ? `Hi ${user.displayName},` : 'Welcome,'} sorted by battery health.
          </p>
        </div>
        <div className="text-xs text-neutral-400">You are rank 4</div>
      </header>

      {myDevices.isLoading && <Spinner />}
      {myDevices.isError && (
        <Card className="text-accent-red">Could not load your devices.</Card>
      )}
      {myDevices.isSuccess && sorted.length === 0 && (
        <Card>
          <div className="text-neutral-300 mb-2">No devices tracked yet.</div>
          <Link to="/" className="text-lime-400 hover:underline text-sm">
            Browse the catalog →
          </Link>
        </Card>
      )}
      {myDevices.isSuccess && sorted.length > 0 && (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sorted.map((ud) => (
            <li key={ud._id}>
              <Link to={`/devices/${ud.deviceId}`} className="card block hover:border-lime-500">
                <div className="text-xs uppercase text-neutral-400">Tracked device</div>
                <div className="text-lg font-semibold mb-3">{ud.nickname || ud.deviceId}</div>
                <BatteryBar percent={ud.batteryHealthPct} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
